'use client'

import Link from 'next/link'
import {useTranslations} from 'next-intl'
import {useState} from 'react'
import {LogOut, Settings} from 'lucide-react'
import {LangSwitch} from '@/design-system'
import {api} from '@/lib/api/client'
import {APP_LOCALES} from '@/lib/locales'
import type {Locale, Me} from '@/lib/api/types'
import {useLocaleSwitch} from '@/hooks/useLocaleSwitch'

/**
 * Sticky app header (T-WEB-08).
 *
 * Reuses the landing header's wordmark and `LangSwitch` so the two chromes
 * read as one product. The switcher offers every locale in `APP_LOCALES` —
 * all three, now that `ru.json` carries the app namespaces and not just the
 * landing one. Anything offered here must have a full catalogue behind it:
 * a language that silently falls back to Uzbek is worse than no option.
 */
export function AppHeader({me, locale}: {me: Me; locale: Locale}) {
  const t = useTranslations('shell')
  const [menuOpen, setMenuOpen] = useState(false)
  const {value: chosenLocale, pending: switching, switchTo} = useLocaleSwitch(locale)

  async function logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      // Even if the call failed, sending them to the landing page is the
      // right end state: the cookie is either cleared or about to be.
      window.location.href = `/${locale}`
    }
  }

  return (
    <header className="u-app__header">
      <div className="u-app__bar">
        <Link
          href={`/${locale}/dashboard`}
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--weight-extrabold)',
            fontSize: 'var(--text-lg)',
            color: 'var(--ink-1)',
            textDecoration: 'none'
          }}
        >
          Uniap
        </Link>

        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8}}>
          <LangSwitch
            value={chosenLocale}
            onChange={switchTo}
            languages={APP_LOCALES}
            label={t('languageLabel')}
            pending={switching}
          />

          <div style={{position: 'relative'}}>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label={t('accountMenu')}
              style={{
                width: 44,
                height: 44,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-full)',
                border: '1px solid var(--border-1)',
                background: 'var(--surface-card)',
                cursor: 'pointer',
                overflow: 'hidden',
                padding: 0
              }}
            >
              {me.avatar_url ? (
                // Not next/image: Google's avatar host would need a remote
                // pattern allowlist and an optimiser round trip for a 32px
                // image already sized by the CDN.
                // eslint-disable-next-line @next/next/no-img-element
                <img src={me.avatar_url} alt="" width={32} height={32} style={{borderRadius: '50%'}} />
              ) : (
                <span aria-hidden="true" style={{fontWeight: 700}}>
                  {(me.name ?? me.email).slice(0, 1).toUpperCase()}
                </span>
              )}
            </button>

            {menuOpen ? (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 52,
                  minWidth: 200,
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-1)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-float)',
                  padding: 6,
                  display: 'grid',
                  gap: 2
                }}
              >
                <Link
                  role="menuitem"
                  href={`/${locale}/settings`}
                  className="u-app__navlink"
                  onClick={() => setMenuOpen(false)}
                >
                  <Settings aria-hidden="true" size={18} />
                  {t('settings')}
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  onClick={logout}
                  className="u-app__navlink"
                  style={{border: 0, background: 'none', cursor: 'pointer', width: '100%'}}
                >
                  <LogOut aria-hidden="true" size={18} />
                  {t('logout')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  )
}
